/**
 * Container query + mutation hooks. Aligned 1-to-1 with the API methods
 * in `api/containers.ts`. Mutations always invalidate the relevant
 * query keys so the UI never shows stale data after a write.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  containersApi,
  type CreateContainerInput,
  type JoinContainerInput,
  type MilestoneType,
  type DeliveryMode,
  type TransportMode,
} from '@/api/containers';

const POLL_MS = 30_000;

// ─── Public marketplace ─────────────────────────────────────────────

export function useOpenContainers(params?: {
  originCountry?: string; destinationCountry?: string; transportMode?: TransportMode;
}) {
  return useQuery({
    queryKey: ['containers', 'open', params?.originCountry, params?.destinationCountry, params?.transportMode],
    queryFn: () => containersApi.listOpen(params),
  });
}

export function useContainerAvailability(params?: {
  originCountry?: string; destinationCountry?: string; transportModes?: string; categoryKey?: string;
}) {
  return useQuery({
    queryKey: ['containers', 'availability', params],
    queryFn: () => containersApi.availability(params),
  });
}

// ─── Sender / participant side ──────────────────────────────────────

export function useMyParticipants() {
  return useQuery({
    queryKey: ['containers', 'participants', 'my'],
    queryFn: () => containersApi.myParticipants(),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useParticipant(pid: number | null) {
  return useQuery({
    queryKey: ['containers', 'participants', 'detail', pid],
    queryFn: () => containersApi.findParticipant(pid!),
    enabled: pid != null,
    refetchInterval: POLL_MS,
  });
}

export function useContainerQuote() {
  return useMutation({
    mutationFn: ({ containerId, ...payload }: {
      containerId: number;
      estWeightKg: number; estCbm?: number; declaredValueUsd: number;
      insuranceOptedIn?: boolean; deliveryMode?: DeliveryMode; destinationCityId?: number;
    }) => containersApi.getQuote(containerId, payload),
  });
}

export function useJoinContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, ...input }: { containerId: number } & JoinContainerInput) =>
      containersApi.join(containerId, input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useUploadDispatchProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, photos, mainPhotoUrl, note }: {
      pid: number; photos: string[]; mainPhotoUrl?: string; note?: string;
    }) => containersApi.uploadDispatchProof(pid, { photos, mainPhotoUrl, note }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers', 'participants'] }); },
  });
}

export function useWithdrawParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, reason }: { pid: number; reason?: string }) =>
      containersApi.withdrawParticipant(pid, reason),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

// ─── Organizer side ─────────────────────────────────────────────────

export function useMyContainers() {
  return useQuery({
    queryKey: ['containers', 'my'],
    queryFn: () => containersApi.listMyContainers(),
    refetchInterval: POLL_MS,
  });
}

export function useContainer(id: number | null) {
  return useQuery({
    queryKey: ['containers', 'detail', id],
    queryFn: () => containersApi.findById(id!),
    enabled: id != null,
    refetchInterval: POLL_MS,
  });
}

export function useContainerParticipants(containerId: number | null) {
  return useQuery({
    queryKey: ['containers', 'participants', containerId],
    queryFn: () => containersApi.listParticipants(containerId!),
    enabled: containerId != null,
    refetchInterval: POLL_MS,
  });
}

export function useContainerEvents(id: number | null) {
  return useQuery({
    queryKey: ['containers', 'events', id],
    queryFn: () => containersApi.listEvents(id!),
    enabled: id != null,
  });
}

export function useContainerDocuments(id: number | null) {
  return useQuery({
    queryKey: ['containers', 'documents', id],
    queryFn: () => containersApi.listDocuments(id!),
    enabled: id != null,
  });
}

// ─── Organizer mutations ────────────────────────────────────────────

export function useCreateContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContainerInput) => containersApi.create(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function usePublishContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => containersApi.publish(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useCloseContainerJoins() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => containersApi.closeJoins(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useSealContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: {
      id: number; sealNumber: string; bookingRef: string; bolNumber: string;
      bolPdfUrl?: string; commercialInvoiceUrl?: string; packingListUrl?: string; carrier?: string;
    }) => containersApi.seal(id, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useAddMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: {
      id: number; type: MilestoneType; note?: string;
      photoUrl?: string; locationText?: string;
    }) => containersApi.addMilestone(id, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useCancelContainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      containersApi.cancel(id, reason),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useApproveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, pid, quotedPriceUsd }: {
      containerId: number; pid: number; quotedPriceUsd?: number;
    }) => containersApi.approveParticipant(containerId, pid, { quotedPriceUsd }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useRejectParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, pid, reason }: { containerId: number; pid: number; reason: string }) =>
      containersApi.rejectParticipant(containerId, pid, reason),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useConfirmWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, pid, ...payload }: {
      containerId: number; pid: number;
      actualWeightKg?: number; actualCbm?: number; actualPiecesCount?: number;
      photos?: string[]; weightTicketUrl?: string; note?: string;
    }) => containersApi.confirmWarehouse(containerId, pid, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useRejectWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, pid, reason, photos }: {
      containerId: number; pid: number; reason: string; photos?: string[];
    }) => containersApi.rejectWarehouse(containerId, pid, { reason, photos }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}

export function useSetCustomsFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ containerId, pid, ...payload }: {
      containerId: number; pid: number; feeUsd?: number; receiptUrl?: string;
    }) => containersApi.setCustomsFee(containerId, pid, payload),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['containers'] }); },
  });
}
